import { useCallback, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Image, Edit, Trash2, Loader2, Plus, UploadCloud, CheckCircle2 } from 'lucide-react';
import { downscaleImage } from '../../lib/imageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GALLERY_CATEGORIES = [
  { value: 'resin', label: 'Resin Art' },
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'textiles', label: 'Textiles' },
  { value: 'woodwork', label: 'Woodwork' },
  { value: 'ceramics', label: 'Ceramics' },
  { value: 'home-decor', label: 'Home Decor' },
  { value: 'fine-art', label: 'Fine Art' },
  { value: 'events', label: 'Events' },
];

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

function titleFromFilename(name) {
  const stem = name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
  if (!stem) return 'Untitled';
  return stem.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

export default function GalleryTab({
  token,
  galleryStats,
  galleryItems,
  isLoadingGallery,
  setShowAddGalleryDialog,
  setEditingGalleryItem,
  setDeleteGalleryConfirm,
  onUploaded,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [queue, setQueue] = useState([]); // [{id, name, status: 'uploading'|'done'|'error', error?}]
  const dragCounter = useRef(0);
  const authHeaders = { Authorization: `Bearer ${token}` };

  const uploadOne = useCallback(async (file) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const entry = { id, name: file.name, status: 'uploading' };
    setQueue((q) => [...q, entry]);

    try {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        throw new Error('Unsupported file type');
      }
      if (file.size > 25 * 1024 * 1024) {
        throw new Error('Image too large (max 25 MB)');
      }
      // Downscale large photos in the browser so they upload cleanly on slow
      // connections and stay under the backend size cap. Small images pass through.
      let uploadFile = file;
      try {
        uploadFile = await downscaleImage(file);
      } catch (_) {
        // If downscale fails for any reason, upload the original — the backend
        // will accept it if under the 25 MB cap, or reject with a clear error.
        uploadFile = file;
      }
      // Step 1: upload to persistent storage
      const form = new FormData();
      form.append('file', uploadFile);
      const upRes = await fetch(`${API}/admin/gallery/upload`, {
        method: 'POST',
        headers: authHeaders,
        body: form,
      });
      if (!upRes.ok) {
        const err = await upRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const upJson = await upRes.json();

      // Step 2: create the gallery item with sensible defaults
      const createRes = await fetch(`${API}/admin/gallery`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: titleFromFilename(file.name),
          image_url: upJson.url,
          category: 'home-decor',
          description: null,
          artisan_name: null,
        }),
      });
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not add to gallery');
      }

      setQueue((q) => q.map((e) => (e.id === id ? { ...e, status: 'done' } : e)));
    } catch (err) {
      setQueue((q) =>
        q.map((e) =>
          e.id === id ? { ...e, status: 'error', error: err.message || 'Failed' } : e
        )
      );
    }
  }, [token]);

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
      if (!files.length) {
        toast.error('Please drop image files only.');
        return;
      }
      // Upload in parallel — the storage endpoint is fine with concurrency.
      await Promise.all(files.map((f) => uploadOne(f)));
      // Refresh the gallery grid
      if (onUploaded) onUploaded();
      // Toast a summary after a brief delay so the queue state settles
      setTimeout(() => {
        setQueue((current) => {
          const ok = current.filter((e) => e.status === 'done').length;
          const failed = current.filter((e) => e.status === 'error').length;
          if (ok) toast.success(`${ok} image${ok !== 1 ? 's' : ''} added to gallery.`);
          if (failed) toast.error(`${failed} upload${failed !== 1 ? 's' : ''} failed.`);
          // Clear entries after the summary; keep errors visible a bit longer
          const keepErrors = current.filter((e) => e.status === 'error');
          return keepErrors;
        });
      }, 600);
    },
    [uploadOne, onUploaded]
  );

  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types && Array.from(e.dataTransfer.types).includes('Files')) {
      dragCounter.current += 1;
      setIsDragging(true);
    }
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  return (
    <div
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className="relative"
      data-testid="gallery-tab-dropzone"
    >
      {/* Drop overlay */}
      <AnimatePresence>
        {isDragging && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center"
            data-testid="gallery-drop-overlay"
          >
            <div className="absolute inset-0 bg-primary/20 backdrop-blur-sm" />
            <div className="relative bg-white rounded-3xl px-10 py-8 border-2 border-dashed border-primary shadow-2xl text-center max-w-md">
              <UploadCloud className="w-14 h-14 text-primary mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-editorial text-2xl mb-1">Drop to add to the gallery</h3>
              <p className="text-sm text-muted-foreground">
                We&apos;ll store them safely so they survive every redeploy.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload queue */}
      {queue.length > 0 && (
        <div
          className="fixed bottom-6 right-6 z-40 bg-white border border-primary/20 shadow-xl rounded-xl p-3 w-80 max-h-72 overflow-y-auto"
          data-testid="gallery-upload-queue"
        >
          <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">
            Uploading
          </p>
          <ul className="space-y-1.5">
            {queue.map((entry) => (
              <li
                key={entry.id}
                className="flex items-start gap-2 text-sm"
                data-testid={`upload-entry-${entry.status}`}
              >
                {entry.status === 'uploading' && (
                  <Loader2 className="w-4 h-4 mt-0.5 animate-spin text-primary flex-shrink-0" />
                )}
                {entry.status === 'done' && (
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                )}
                {entry.status === 'error' && (
                  <div className="w-4 h-4 mt-0.5 flex-shrink-0 rounded-full bg-destructive text-white flex items-center justify-center text-[10px]">
                    !
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate" title={entry.name}>{entry.name}</p>
                  {entry.status === 'error' && entry.error && (
                    <p className="text-xs text-destructive truncate" title={entry.error}>
                      {entry.error}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-6">
        {/* Gallery Stats + Upload hint */}
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Image className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gallery Images</p>
                <p className="text-2xl font-semibold">
                  {galleryStats.total} / {galleryStats.max_items}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden md:inline-flex items-center gap-1 text-xs text-muted-foreground">
                <UploadCloud className="w-3.5 h-3.5" />
                Drop images anywhere on this tab to add them
              </span>
              {!galleryStats.can_add && <Badge variant="destructive">Gallery Full</Badge>}
            </div>
          </div>
        </div>

        {/* Gallery Grid */}
        {isLoadingGallery ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : galleryItems.length === 0 ? (
          <div
            className="bg-white rounded-xl border-2 border-dashed p-12 text-center"
            data-testid="gallery-empty-state"
          >
            <UploadCloud className="w-12 h-12 mx-auto text-primary/60 mb-4" strokeWidth={1.5} />
            <p className="font-editorial text-2xl mb-1">No gallery images yet</p>
            <p className="text-sm text-muted-foreground mb-6">
              Drop photos anywhere on this tab, or add them one at a time.
            </p>
            <Button onClick={() => setShowAddGalleryDialog(true)} data-testid="gallery-add-first-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add First Image
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {galleryItems.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl border overflow-hidden group"
                data-testid={`gallery-item-${item.id}`}
              >
                <div className="aspect-square relative overflow-hidden">
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    onError={(e) => {
                      if (!e.target.dataset.fallback) {
                        e.target.dataset.fallback = '1';
                        e.target.src =
                          'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=800&q=80&auto=format&fit=crop';
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setEditingGalleryItem(item)}
                        data-testid={`edit-gallery-${item.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setDeleteGalleryConfirm(item)}
                        data-testid={`delete-gallery-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="font-medium text-sm truncate">{item.title}</h3>
                  <div className="flex items-center justify-between mt-1">
                    <Badge variant="outline" className="text-xs">
                      {GALLERY_CATEGORIES.find((c) => c.value === item.category)?.label ||
                        item.category}
                    </Badge>
                    {item.artisan_name && (
                      <span className="text-xs text-muted-foreground truncate ml-2">
                        {item.artisan_name}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
